import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  CheckCircle as ApprovedIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAppSelector } from '../../store';
import { PurchaseRequest, RequestStatus } from '../../types/request.types';
import { API_CONFIG } from '../../config/api';

interface FinancialStats {
  totalApprovedRequests: number;
  totalPaidRequests: number;
  totalApprovedAmount: number;
  totalPaidAmount: number;
  averageApprovedAmount: number;
  averagePaidAmount: number;
  pendingPaymentAmount: number;
}

const FinanceReports: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector(state => state.auth.user);
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const [financialStats, setFinancialStats] = useState<FinancialStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_CONFIG.BASE_URL}/requests`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch financial data');
      }

      const allRequests: PurchaseRequest[] = await response.json();
      setRequests(allRequests);

      // Calculate financial statistics
      calculateFinancialStats(allRequests);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const calculateFinancialStats = (allRequests: PurchaseRequest[]) => {
    const approvedRequests = allRequests.filter(r => 
      r.status === RequestStatus.APPROVED || 
      r.status === RequestStatus.PAID || 
      r.status === RequestStatus.CLOSED
    );
    
    const paidRequests = allRequests.filter(r => 
      r.status === RequestStatus.PAID || 
      r.status === RequestStatus.CLOSED
    );

    const pendingPaymentRequests = allRequests.filter(r => r.status === RequestStatus.APPROVED);

    const totalApprovedAmount = approvedRequests.reduce((sum, r) => sum + r.amount, 0);
    const totalPaidAmount = paidRequests.reduce((sum, r) => sum + r.amount, 0);
    const pendingPaymentAmount = pendingPaymentRequests.reduce((sum, r) => sum + r.amount, 0);

    setFinancialStats({
      totalApprovedRequests: approvedRequests.length,
      totalPaidRequests: paidRequests.length,
      totalApprovedAmount,
      totalPaidAmount,
      averageApprovedAmount: approvedRequests.length > 0 ? totalApprovedAmount / approvedRequests.length : 0,
      averagePaidAmount: paidRequests.length > 0 ? totalPaidAmount / paidRequests.length : 0,
      pendingPaymentAmount,
    });
  };

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.APPROVED:
        return 'success';
      case RequestStatus.PAID:
        return 'info';
      case RequestStatus.CLOSED:
        return 'secondary';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Financial Reports
        </Typography>
      </Box>

      {/* Financial Statistics */}
      {financialStats && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon color="primary" />
            Financial Overview
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'success.50' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <ApprovedIcon color="success" />
                    <Box>
                      <Typography variant="h4" color="success.main">
                        {financialStats.totalApprovedRequests}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Approved Requests
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'info.50' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <PaymentIcon color="info" />
                    <Box>
                      <Typography variant="h4" color="info.main">
                        {financialStats.totalPaidRequests}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Paid Requests
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'warning.50' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <ReceiptIcon color="warning" />
                    <Box>
                      <Typography variant="h4" color="warning.main">
                        {formatCurrency(financialStats.pendingPaymentAmount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pending Payment
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Total Approved Amount
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {formatCurrency(financialStats.totalApprovedAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Total Paid Amount
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(financialStats.totalPaidAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Average Request Value
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {formatCurrency(financialStats.averageApprovedAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Recent Financial Transactions */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon color="primary" />
          Recent Financial Transactions
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Request Title</strong></TableCell>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell><strong>Requester</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests
                .filter(request => 
                  request.status === RequestStatus.APPROVED || 
                  request.status === RequestStatus.PAID || 
                  request.status === RequestStatus.CLOSED
                )
                .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                .slice(0, 20)
                .map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.department.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.requester.firstName} {request.requester.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(request.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={request.status.replace(/_/g, ' ')}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(request.updatedAt), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {requests.filter(r => 
          r.status === RequestStatus.APPROVED || 
          r.status === RequestStatus.PAID || 
          r.status === RequestStatus.CLOSED
        ).length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No financial transactions found.
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default FinanceReports; 